import { logger } from '../../utils/logger';
import React, { useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, UserIcon, ClockIcon, MessageSquareIcon, AlertTriangleIcon, StickyNoteIcon, ClipboardListIcon, DownloadIcon, ArchiveIcon, PowerOffIcon, CheckCircleIcon, RotateCcwIcon, XCircleIcon, LockIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { Pagination } from '../ui/Pagination';
import { useData } from '../../contexts/DataContext';
import { usePermissions } from '../../hooks/usePermissions';
import { usePagination } from '../../hooks/usePagination';
import { useSortableData } from '../../hooks/useSortableData';
import { SortableHeader } from '../ui/SortableHeader';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { FollowUpNote } from '../../types';
import { exportFollowUpNotesToExcel } from '../../utils/excelExport';
import { exportFollowUpNotesToPdf } from '../../utils/pdfExport';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
// ============ SAFE STRING HELPER ============
// Guarantees any value becomes a renderable string - NEVER an object
function ss(v: any): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (v instanceof Date) {
    try {
      return v.toISOString();
    } catch {
      return '';
    }
  }
  try {
    return JSON.stringify(v);
  } catch {
    return '';
  }
}
// Safe date formatter
function safeFmtDate(date: any): string {
  try {
    if (!date) return 'Sin fecha';
    const d = date instanceof Date ? date : new Date(typeof date === 'string' || typeof date === 'number' ? date : Date.now());
    if (isNaN(d.getTime())) return 'Fecha inválida';
    return d.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    }) + ' • ' + d.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return 'Sin fecha';
  }
}
// Sanitize a single note - every field becomes a safe primitive
function sanitizeNote(raw: any): {
  id: string;
  userId: string;
  userName: string;
  contactName: string;
  subject: string;
  description: string;
  createdAt: Date;
} {
  if (!raw || typeof raw !== 'object') {
    return {
      id: '',
      userId: '',
      userName: '',
      contactName: '',
      subject: '',
      description: '',
      createdAt: new Date()
    };
  }
  return {
    id: ss(raw.id),
    userId: ss(raw.userId),
    userName: ss(raw.userName),
    contactName: ss(raw.contactName),
    subject: ss(raw.subject),
    description: ss(raw.description),
    createdAt: raw.createdAt instanceof Date ? raw.createdAt : new Date(typeof raw.createdAt === 'string' || typeof raw.createdAt === 'number' ? raw.createdAt : Date.now())
  };
}
export function FollowUpNotes() {
  // ============ DATA ============
  const dataCtx = useData();
  const permCtx = usePermissions();
  const {
    user
  } = useAuth();
  const {
    showToast
  } = useToast();
  const isAdmin = user?.role === 'administrador';
  const userRoleNorm = user?.role?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const canExportExcel = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor/a' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const canExportPdf = isAdmin || user?.role === 'terapeuta' || user?.role === 'supervisor' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const readOnly = permCtx.isReadOnly('followUpNotes');
  // Sanitize ALL notes immediately - no raw data ever reaches JSX
  const rawNotes = dataCtx.followUpNotes;
  const notes = (Array.isArray(rawNotes) ? rawNotes : []).map(sanitizeNote);
  // ============ STATE ============
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    contactName: '',
    subject: '',
    description: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  const [deleteNoteId, setDeleteNoteId] = useState<string | null>(null);
  // Archive/Deactivate
  const STORAGE_KEY = 'savidna-followup-notes-status';
  const [itemStatuses, setItemStatuses] = useState<Record<string, string>>({});
  const [viewMode, setViewMode] = useState<string>('active');
  const [actionConfirm, setActionConfirm] = useState<{
    id: string;
    type: string;
  } | null>(null);
  const [patientStatuses, setPatientStatuses] = useState<Record<string, string>>({});
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          setItemStatuses(parsed);
        }
      }
      const ps = localStorage.getItem('savidna-patients-status');
      if (ps) {
        const parsed = JSON.parse(ps);
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          setPatientStatuses(parsed);
        }
      }
    } catch (e) {
      logger.warn('Error loading statuses:', e);
    }
  }, []);
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(itemStatuses));
    } catch {}
  }, [itemStatuses]);
  // ============ DERIVED DATA ============
  const currentUserDoctor = dataCtx.doctors.find((d: any) => ss(d.email).toLowerCase() === ss(user?.email).toLowerCase());
  const isDoctor = !!currentUserDoctor && !isAdmin;
  const availablePatients = isDoctor && currentUserDoctor ? dataCtx.patients.filter((p: any) => !patientStatuses[ss(p.id)] && dataCtx.appointments.some((a: any) => ss(a.patientId) === ss(p.id) && ss(a.doctorId) === ss(currentUserDoctor.id) && ss(a.status) === 'completada')) : dataCtx.patients.filter((p: any) => !patientStatuses[ss(p.id)]);
  const getStatus = (id: string): string => itemStatuses[id] || 'active';
  // Filter
  const filteredNotes = notes.filter(note => {
    const term = searchTerm.toLowerCase();
    const matchesSearch = !term || note.contactName.toLowerCase().includes(term) || note.subject.toLowerCase().includes(term) || note.description.toLowerCase().includes(term) || note.userName.toLowerCase().includes(term);
    return matchesSearch && getStatus(note.id) === viewMode;
  });
  const {
    sortedItems: sortedNotes,
    requestSort: noteRequestSort,
    isSorted: noteIsSorted
  } = useSortableData(filteredNotes, {
    key: 'createdAt',
    direction: 'desc'
  });
  // Pagination
  const pagination = usePagination(sortedNotes, {
    itemsPerPage: 10
  });
  // Counts
  const activeCount = notes.filter(n => getStatus(n.id) === 'active').length;
  const archivedCount = notes.filter(n => getStatus(n.id) === 'archived').length;
  const deactivatedCount = notes.filter(n => getStatus(n.id) === 'deactivated').length;
  // ============ HANDLERS ============
  const openNewForm = () => {
    setEditingNoteId(null);
    setFormData({
      contactName: '',
      subject: '',
      description: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (note: ReturnType<typeof sanitizeNote>) => {
    setEditingNoteId(note.id);
    setFormData({
      contactName: note.contactName,
      subject: note.subject,
      description: note.description
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.contactName.trim()) errors.contactName = 'Debe seleccionar un paciente';
    if (!formData.subject.trim()) errors.subject = 'El asunto es requerido';
    if (!formData.description.trim()) errors.description = 'La descripción es requerida';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    try {
      if (editingNoteId) {
        await dataCtx.updateFollowUpNote(editingNoteId, formData);
        showToast({
          type: 'success',
          title: 'Nota actualizada',
          message: 'La nota de seguimiento fue actualizada correctamente.'
        });
      } else {
        await dataCtx.addFollowUpNote({
          userId: ss(user?.id),
          userName: ss(user?.name) || 'Usuario',
          ...formData
        });
        showToast({
          type: 'success',
          title: 'Nota creada',
          message: 'La nota de seguimiento fue registrada correctamente.'
        });
      }
    } catch (err) {
      logger.error('Error saving note:', err);
      showToast({
        type: 'error',
        title: 'Error al guardar',
        message: 'No se pudo guardar la nota de seguimiento.'
      });
    }
    setIsFormOpen(false);
  };
  const handleDelete = async () => {
    if (deleteNoteId) {
      try {
        await dataCtx.deleteFollowUpNote(deleteNoteId);
        showToast({
          type: 'error',
          title: 'Nota eliminada',
          message: 'La nota de seguimiento fue eliminada.'
        });
      } catch (err) {
        logger.error('Error deleting:', err);
        showToast({
          type: 'error',
          title: 'Error al eliminar',
          message: 'No se pudo eliminar la nota.'
        });
      }
      setIsDeleteConfirmOpen(false);
      setDeleteNoteId(null);
    }
  };
  const handleArchive = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'archived'
    }));
    dataCtx.updateFollowUpNote(id, {
      status: 'archived'
    } as any).catch(() => {});
    setActionConfirm(null);
  };
  const handleDeactivate = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'deactivated'
    }));
    dataCtx.updateFollowUpNote(id, {
      status: 'deactivated'
    } as any).catch(() => {});
    setActionConfirm(null);
  };
  const handleRestore = (id: string) => {
    setItemStatuses(prev => {
      const n = {
        ...prev
      };
      delete n[id];
      return n;
    });
    dataCtx.updateFollowUpNote(id, {
      status: 'active'
    } as any).catch(() => {});
  };
  const handleExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsExporting(true);
    await new Promise(r => setTimeout(r, 300));
    try {
      let dataToExport = rawNotes;
      if (dateFrom && dateTo) {
        dataToExport = rawNotes.filter((n: any) => {
          const dateStr = new Date(n.createdAt).toISOString().split('T')[0];
          return dateStr >= dateFrom && dateStr <= dateTo;
        });
      }
      exportFollowUpNotesToExcel(dataToExport);
    } catch (e) {
      logger.error('Export error:', e);
    }
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    let dataToExport = rawNotes;
    if (dateFrom && dateTo) {
      dataToExport = rawNotes.filter((n: any) => {
        const dateStr = new Date(n.createdAt).toISOString().split('T')[0];
        return dateStr >= dateFrom && dateStr <= dateTo;
      });
    }
    exportFollowUpNotesToPdf(dataToExport);
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  // ============ PERMISSION CHECK ============
  if (!permCtx.canView('followUpNotes')) {
    return <div className="flex flex-col items-center justify-center h-[60vh] text-center p-6" data-id="element-3225" data-mp="391hicvk90amh">
        <MessageSquareIcon className="w-16 h-16 text-gray-300 mb-4" data-id="element-3226" data-mp="342ef9gntweh5" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2" data-id="element-3227" data-mp="3413apemk72oz">
          Acceso Restringido
        </h2>
        <p className="text-gray-500 dark:text-gray-400 max-w-md" data-id="element-3228" data-mp="3hzt9qzsxvcfz">
          No tienes permisos para ver las notas de seguimiento.
        </p>
      </div>;
  }
  // ============ RENDER ============
  const deleteNote = deleteNoteId ? notes.find(n => n.id === deleteNoteId) : null;
  return <div className="space-y-6" data-id="element-3229" data-mp="3muz48n0epzfo">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl shadow-xl" style={{
      background: 'linear-gradient(135deg, #d4a870, #e8c08a, #faf0e0)'
    }} data-id="element-3230" data-mp="5jzh56yze1v1">
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: 'radial-gradient(ellipse at center, transparent 30%, rgba(120,60,0,1) 31%, rgba(120,60,0,1) 32%, transparent 33%), radial-gradient(ellipse at center, transparent 60%, rgba(120,60,0,1) 61%, rgba(120,60,0,1) 62%, transparent 63%)',
        backgroundSize: '48px 48px'
      }} data-id="element-3231" data-mp="k77uadbuoh9n" />
        {/* Glow */}
        <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full blur-2xl opacity-30 pointer-events-none" style={{
        background: 'rgba(255,200,100,0.6)'
      }} data-id="element-3232" data-mp="ifk6mknpmw4" />
        {/* Left accent strip */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl" style={{
        background: 'rgba(160,80,10,0.4)'
      }} data-id="element-3233" data-mp="3nphkuv04frix" />
        
        <div className="relative z-10 p-6 pl-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full" data-id="element-3234" data-mp="3dq0hik8cdpq4">
          <div data-id="element-3235" data-mp="3ipmucsk9d3ye">
            <div className="flex items-center gap-3 mb-2" data-id="element-3236" data-mp="3io21r8hi7v36">
              <div className="p-2.5 rounded-xl border backdrop-blur-sm" style={{
              background: 'rgba(180,100,20,0.15)',
              borderColor: 'rgba(180,100,20,0.3)'
            }} data-id="element-3237" data-mp="pumxz4zqnn2z">
                <ClipboardListIcon className="w-6 h-6 text-white" data-id="element-3238" data-mp="g4toc9j5pi1d" />
              </div>
              <h1 className="text-2xl font-bold" style={{
              color: "#fff",
              textShadow: "0 1px 3px rgba(100,60,0,0.25)"
            }} data-id="element-3239" data-mp="g2gyzbb2k5ea">{'Reportes diarios'}</h1>
            </div>
            <p className="max-w-xl" style={{
            color: "rgba(255,255,255,0.85)"
          }} data-id="element-3240" data-mp="64azf0cqjbcl">
              {'Registro de comunicaciones y seguimientos con contactos'}
              {readOnly && <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs font-medium" data-id="element-3241" data-mp="3tdg10nuw4tiw">
                  {'Solo lectura'}
                </span>}
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-3242" data-mp="1rjgqw5ti8i7s">
            {canExportPdf && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-3244" data-mp="1mlow941h6hzw" />} onClick={() => setIsPdfExportModalOpen(true)} className="border hover:opacity-80 transition-opacity" style={{
            background: "rgba(91,200,200,0.2)",
            borderColor: "rgba(91,200,200,0.6)",
            color: "#1a4a4a"
          }} data-id="element-3243" data-mp="1mkm833vjetno">
              
                Exportar PDF
              </Button>}
            {canExportExcel && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-3246" data-mp="28v5fiolxare1" />} onClick={() => setIsExportModalOpen(true)} isLoading={isExporting} className="border hover:opacity-80 transition-opacity" style={{
            background: "rgba(91,200,200,0.2)",
            borderColor: "rgba(91,200,200,0.6)",
            color: "#1a4a4a"
          }} data-id="element-3245" data-mp="28srmelaszp94">
              
                Exportar Excel
              </Button>}
            {permCtx.canCreate('followUpNotes') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-3248" data-mp="2whu41t5fti3m" />} onClick={openNewForm} data-id="element-3247" data-mp="2wjezrm3hn732">
              
                {'Nueva Nota'}
              </Button>}
          </div>
        </div>
      </div>

      {readOnly && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-3249" data-mp="11exmd5s57eun">
          <LockIcon className="w-4 h-4" data-id="element-3250" data-mp="1agx460azemyn" />
          <span data-id="element-3251" data-mp="1afm4abknpzct">
            {'Solo tienes permisos de lectura. No puedes crear, editar o eliminar notas de seguimiento.'}
          </span>
        </div>}

      {/* Search + Tabs */}
      <div className="flex flex-col md:flex-row gap-4 justify-between items-end" data-id="element-3252" data-mp="iq0tn6ce8uej">
        <div className="w-full md:w-96 relative" data-id="element-3253" data-mp="dqxpdo48ms7m">
          <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-3254" data-mp="do1sm344m9kr" />
          <Input style={{
          paddingLeft: "2.5rem"
        }} placeholder="Buscar por paciente, asunto o descripción..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-id="element-3255" data-mp="mr2v7tts9ijo" />
          
        </div>
        <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-full md:w-auto overflow-x-auto" data-id="element-3256" data-mp="3h141kwgtg3ie">
          {[{
          key: 'active',
          label: 'Activos',
          count: activeCount,
          Icon: CheckCircleIcon
        }, {
          key: 'archived',
          label: 'Archivados',
          count: archivedCount,
          Icon: ArchiveIcon
        }, {
          key: 'deactivated',
          label: 'Desactivados',
          count: deactivatedCount,
          Icon: XCircleIcon
        }].map(tab => <button key={tab.key} onClick={() => setViewMode(tab.key)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${viewMode === tab.key ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-3257" data-mp="8jzkeqoc60eg">
            
              <tab.Icon className="w-4 h-4" data-id="element-3258" data-mp="2jdvdqutceid8" />
              {tab.label}
              <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full" data-id="element-3259" data-mp="2jazhdax581qw">
                {String(tab.count)}
              </span>
            </button>)}
        </div>
      </div>

      {/* Table */}
      <Card padding="none" className="overflow-hidden border-0 shadow-sm ring-1 ring-gray-200/80 dark:ring-gray-700/60" data-id="element-3260" data-mp="1qodhss4pufqy">
        <div className="h-1.5" style={{
        background: "linear-gradient(90deg,#5bc8c8,#3aacac,#5bc8c8)"
      }} data-id="element-3261" data-mp="1mo3a9b19zh9z" />
        {/* ── MOBILE CARDS ── */}
        <div className="sm:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-3262" data-mp="2jf56l4jgc249">
          {pagination.paginatedItems.length === 0 ? <div className="py-10 text-center text-gray-500 dark:text-gray-400" data-id="element-3263" data-mp="29mzfdx10xoq4">
              <p className="text-sm" data-id="element-3264" data-mp="25lw0bcqsvwpy">No hay notas</p>
            </div> : pagination.paginatedItems.map(note => <div key={note.id || Math.random()} className="px-4 py-3 flex items-start gap-3" data-id="element-3265" data-mp="323savktt0ngs">
            
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{
            background: "#5bc8c8"
          }} data-id="element-3266" data-mp="2xdbubruoslq7">
                  {(note.userName || '?').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0" data-id="element-3267" data-mp="ovdd9t1j20ld">
                  <p className="font-semibold text-gray-900 dark:text-white text-sm truncate" data-id="element-3268" data-mp="su2lz8bmetyo">
                    {note.contactName || '-'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate" data-id="element-3269" data-mp="12tjsfryp5bvp">
                    {note.userName} · {safeFmtDate(note.createdAt)}
                  </p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap" data-id="element-3270" data-mp="17qa5e6f7frcw">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold text-xs font-semibold" style={{
                background: '#fddbb4',
                color: '#7c4b1a'
              }} data-id="element-3271" data-mp="1cjmvym3g288a">
                      {note.subject}
                    </span>
                    {note.description && <p className="text-xs text-gray-400 truncate max-w-[180px]" data-id="element-3272" data-mp="1qj5tym2jk36v">
                        {note.description}
                      </p>}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0" data-id="element-3273" data-mp="1jmr6txulvbbv">
                  {viewMode === 'active' && permCtx.canEdit('followUpNotes') && <button onClick={() => openEditForm(note)} className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" data-id="element-3274" data-mp="1tkdq0wixtp1n">
                
                        <EditIcon className="w-4 h-4" data-id="element-3275" data-mp="1yhxeep9cni7b" />
                      </button>}
                  {viewMode === 'active' && <button onClick={() => setActionConfirm({
              id: note.id,
              type: 'archive'
            })} className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors" data-id="element-3276" data-mp="27c1zst5ps1fg">
                
                      <ArchiveIcon className="w-4 h-4" data-id="element-3277" data-mp="2hbj2r2tl017q" />
                    </button>}
                  {viewMode !== 'active' && <button onClick={() => handleRestore(note.id)} className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors" data-id="element-3278" data-mp="2m9uc7cfys9pf">
                
                      <RotateCcwIcon className="w-4 h-4" data-id="element-3279" data-mp="5m36fq2e9i8o" />
                    </button>}
                </div>
              </div>)}
        </div>

        {/* ── DESKTOP TABLE ── */}
        <div className="hidden sm:block overflow-x-auto" data-id="element-3280" data-mp="3rum8674fjuls">
          <table className="w-full" data-id="element-3281" data-mp="3rw77sb5lq3qi">
            <thead data-id="element-3282" data-mp="td06a4gx0p3k">
              <tr className="border-b-2 border-[#5bc8c8]/30 dark:border-gray-700" style={{
              background: "rgba(91,200,200,0.08)"
            }} data-id="element-3283" data-mp="tbfdoke5vg8c">
                <SortableHeader label="Fecha y Hora" sortKey="createdAt" currentDirection={noteIsSorted('createdAt')} onSort={noteRequestSort} data-id="element-3284" data-mp="jmnpmnc81g2p" />
                
                <SortableHeader label="Registrado por" sortKey="userName" currentDirection={noteIsSorted('userName')} onSort={noteRequestSort} className="hidden sm:table-cell" data-id="element-3285" data-mp="en1ag8cu1pr9" />
                
                <SortableHeader label="Paciente" sortKey="contactName" currentDirection={noteIsSorted('contactName')} onSort={noteRequestSort} data-id="element-3286" data-mp="17aovfoi0mief" />
                
                <SortableHeader label="Asunto" sortKey="subject" currentDirection={noteIsSorted('subject')} onSort={noteRequestSort} data-id="element-3287" data-mp="36joswsc9q3o7" />
                
                <th className="text-left py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden sm:table-cell" data-id="element-3288" data-mp="2rs6e2xv5af6f">
                  {'Descripción'}
                </th>
                <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-3289" data-mp="2ivyrrew3aies">
                  {'Acciones'}
                </th>
              </tr>
            </thead>
            <tbody className="" data-id="element-3290" data-mp="2du9dlqdklomh">
              {pagination.paginatedItems.length === 0 ? <tr data-id="element-3291" data-mp="2dwce54ccn05g">
                  <td colSpan={6} className="py-16 text-center text-gray-500 dark:text-gray-400" data-id="element-3292" data-mp="74bb79nqjb5o">
                  
                    <div className="flex flex-col items-center justify-center" data-id="element-3293" data-mp="c3ei6zn9zrga">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4" data-id="element-3294" data-mp="1wkxce09oii0o">
                        <StickyNoteIcon className="w-8 h-8 text-gray-400" data-id="element-3295" data-mp="1rn5empmcs1ok" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1" data-id="element-3296" data-mp="26bs2jfq1q1re">
                        {'No se encontraron notas'}
                      </h3>
                      <p className="text-sm" data-id="element-3297" data-mp="2fb5au52fokaj">
                        {searchTerm ? 'Intenta con otros términos de búsqueda' : viewMode === 'active' ? 'Comienza creando una nueva nota' : 'No hay notas en esta vista'}
                      </p>
                    </div>
                  </td>
                </tr> : pagination.paginatedItems.map(note => <tr key={note.id || Math.random()} className="hover:bg-[#5bc8c8]/5 dark:hover:bg-teal-900/10 transition-colors duration-150 group border-b border-gray-50 dark:border-gray-800/50 last:border-0" data-id="element-3298" data-mp="2kbay791o7kmt">
                
                    <td className="py-3 px-4 whitespace-nowrap" data-id="element-3299" data-mp="pz39idfxzzzh">
                      <div className="flex flex-col gap-0.5" data-id="element-3300" data-mp="3d4o762yt9o79">
                        <span className="text-sm font-semibold text-gray-800 dark:text-gray-200" data-id="element-3301" data-mp="3i3rbfl6yvqe6">{safeFmtDate(note.createdAt)}</span>
                        <span className="text-[11px] text-gray-400 flex items-center gap-1" data-id="element-3302" data-mp="3n4oqhowok4bg">
                          <ClockIcon className="w-3 h-3" data-id="element-3303" data-mp="3r3nwzdy28950" />
                          {note.createdAt instanceof Date ? note.createdAt.toLocaleTimeString('es-ES', {
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : ''}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap hidden sm:table-cell" data-id="element-3304" data-mp="38638i7z7jckx">
                      <div className="flex items-center gap-2" data-id="element-3305" data-mp="387xu7oiaz8q2">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 ring-2 ring-white dark:ring-gray-800" style={{
                    background: "#5bc8c8"
                  }} data-id="element-3306" data-mp="2bnp2h5hd8bn8">
                          {(note.userName || '?').charAt(0).toUpperCase()}
                        </div>
                        <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-3307" data-mp="1z5cnnqoyw4po">
                          {note.userName}
                        </span>
                      </div>
                    </td>
                    <td className="py-3 px-4 whitespace-nowrap" data-id="element-3308" data-mp="1p7yk2ro57aes">
                      <span className="text-sm font-semibold text-gray-900 dark:text-white" data-id="element-3309" data-mp="1p99n2p9rke4u">
                        {note.contactName || '—'}
                      </span>
                    </td>
                    <td className="py-3 px-4" data-id="element-3310" data-mp="1ffixfh1whxnk">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={{
                  background: '#e8f9f9',
                  color: '#1a6b6b',
                  border: '1px solid #5bc8c8'
                }} data-id="element-3311" data-mp="1bgthphte440r">
                        {note.subject}
                      </span>
                    </td>
                    <td className="py-3 px-4 hidden sm:table-cell max-w-xs" data-id="element-3312" data-mp="rwl20aivr3zs">
                      <p className="text-sm text-gray-600 dark:text-gray-400 truncate lowercase first-letter:uppercase" title={note.description} data-id="element-3313" data-mp="wumnt37oxhey">
                        {note.description}
                      </p>
                    </td>
                    <td className="py-3 px-4" data-id="element-3314" data-mp="1akzvd4go6u3r">
                      <div className="flex items-center justify-end gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-3315" data-mp="1al88mxgpg1fh">
                        {viewMode === 'active' ? <>
                            {permCtx.canEdit('followUpNotes') && <button onClick={() => openEditForm(note)} className="p-1.5 text-gray-400 hover:text-[#1a6b6b] hover:bg-[#5bc8c8]/10 rounded-lg transition-colors" title="Editar" data-id="element-3316" data-mp="10su0nmz19ykv">
                        
                                <EditIcon className="w-4 h-4" data-id="element-3317" data-mp="1yaap41zlro8e" />
                              </button>}
                            <button onClick={() => setActionConfirm({
                      id: note.id,
                      type: 'archive'
                    })} className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors" title="Archivar" data-id="element-3318" data-mp="3j0g1ydg6tp29">
                        
                              <ArchiveIcon className="w-4 h-4" data-id="element-3319" data-mp="3nshcnuxpj7q6" />
                            </button>
                            <button onClick={() => setActionConfirm({
                      id: note.id,
                      type: 'deactivate'
                    })} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Desactivar" data-id="element-3320" data-mp="302mxv7ttv93a">
                        
                              <PowerOffIcon className="w-4 h-4" data-id="element-3321" data-mp="35318mxmtcx50" />
                            </button>
                          </> : <button onClick={() => handleRestore(note.id)} className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Restaurar" data-id="element-3322" data-mp="oe5hpdzjyq57">
                      
                            <RotateCcwIcon className="w-4 h-4" data-id="element-3323" data-mp="2irq4tzv0axnb" />
                          </button>}
                      </div>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
        <div className="px-6 pb-4 border-t border-gray-100 dark:border-gray-700 pt-4" data-id="element-3324" data-mp="2sj47ggi98dko">
          <Pagination currentPage={pagination.currentPage} totalPages={pagination.totalPages} totalItems={pagination.totalItems} startIndex={pagination.startIndex} endIndex={pagination.endIndex} onPageChange={pagination.goToPage} onNext={pagination.nextPage} onPrev={pagination.prevPage} data-id="element-3325" data-mp="2wiw9sbh21nhl" />
          
        </div>
      </Card>

      {/* Form Modal */}
      {isFormOpen && <Modal isOpen={true} onClose={() => setIsFormOpen(false)} title={editingNoteId ? 'Editar Nota' : 'Nueva Nota de Seguimiento'} size="md" icon={<MessageSquareIcon className="w-5 h-5" data-id="element-3327" data-mp="1h3yrwbx0ykjn" />} accentColor="amber" data-id="element-3326" data-mp="1c4ceo2h754au">
        
          <form onSubmit={handleSubmit} className="space-y-6" data-id="element-3328" data-mp="3udp1urwtbnhs">
            <div data-id="element-3329" data-mp="3pmqmbujnetfl">
              <div className="flex items-center gap-2 mb-4" data-id="element-3330" data-mp="3pm738ai9hyzz">
                <span className="w-1.5 h-5 rounded-full rounded-full flex-shrink-0" style={{
              background: "#5bc8c8",
              width: "6px",
              height: "6px"
            }} data-id="element-3331" data-mp="3pkm9gn3ruk36"></span>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-3332" data-mp="3gk7jhafv4gtc">
                  {'Información de la Nota'}
                </h3>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-3333" data-mp="d37lx3jl7oju">
                <Select label="Paciente" options={[{
              value: '',
              label: 'Seleccionar paciente...'
            }, ...availablePatients.map((p: any) => ({
              value: ss(p.name),
              label: ss(p.name)
            }))]} value={formData.contactName} onChange={v => setFormData({
              ...formData,
              contactName: v
            })} error={formErrors.contactName} data-id="element-3334" data-mp="37nqsjcuktw5" />
              
                <Input label="Asunto" value={formData.subject} onChange={e => setFormData({
              ...formData,
              subject: e.target.value
            })} error={formErrors.subject} placeholder="Ej: Llamada de seguimiento, Correo enviado..." data-id="element-3335" data-mp="1h8720ojt6vgp" />
              
              </div>
            </div>
            <div data-id="element-3336" data-mp="24yb60x0wsx88">
              <div className="flex items-center gap-2 mb-4" data-id="element-3337" data-mp="1zx3ymz2oi023">
                <span className="w-1.5 h-5 rounded-full rounded-full flex-shrink-0" style={{
              background: "#3aacac",
              width: "6px",
              height: "6px"
            }} data-id="element-3338" data-mp="1zwlxpp52mxnu"></span>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-3339" data-mp="29w2vzlhzu99s">
                  {'Descripción'}
                </h3>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 border border-gray-100 dark:border-gray-700/50" data-id="element-3340" data-mp="fiuzlpeddpm1">
                <Textarea label="Detalle del seguimiento" value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} error={formErrors.description} placeholder="Ej: Le escribí al paciente pero no respondió..." rows={4} data-id="element-3341" data-mp="5fg5pvjg2feq" />
              
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-3342" data-mp="3htp645ek8j4z">
              <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-3343" data-mp="3mumpumf7xl8l">
              
                {'Cancelar'}
              </Button>
              <Button type="submit" data-id="element-3344" data-mp="3dsvjrggrnce7">
                {editingNoteId ? 'Guardar Cambios' : 'Crear Nota'}
              </Button>
            </div>
          </form>
        </Modal>}

      {/* Action Confirm Modal */}
      {actionConfirm && <Modal isOpen={true} onClose={() => setActionConfirm(null)} title={actionConfirm.type === 'archive' ? 'Archivar Nota' : 'Desactivar Nota'} size="sm" data-id="element-3345" data-mp="1c0qvqg2e0z8r">
        
          <div className="space-y-4" data-id="element-3346" data-mp="15ls1jijcqydo">
            <p className="text-gray-600 dark:text-gray-400" data-id="element-3347" data-mp="15kqppbuii7uw">
              {actionConfirm.type === 'archive' ? '¿Estás seguro de que deseas archivar esta nota? Podrás restaurarla más tarde.' : '¿Estás seguro de que deseas desactivar esta nota? No aparecerá en las listas activas.'}
            </p>
            <div className="flex justify-end gap-3" data-id="element-3348" data-mp="1t9t50d1obom8">
              <Button variant="outline" onClick={() => setActionConfirm(null)} data-id="element-3349" data-mp="1oc1d3j1142i1">
                {'Cancelar'}
              </Button>
              <Button variant="primary" onClick={() => {
            if (actionConfirm.type === 'archive') handleArchive(actionConfirm.id);else handleDeactivate(actionConfirm.id);
          }} data-id="element-3350" data-mp="1jkjg1624mm2c">
              
                {actionConfirm.type === 'archive' ? 'Archivar' : 'Desactivar'}
              </Button>
            </div>
          </div>
        </Modal>}

      {/* Delete Confirm Modal */}
      {isDeleteConfirmOpen && deleteNote && <Modal isOpen={true} onClose={() => {
      setIsDeleteConfirmOpen(false);
      setDeleteNoteId(null);
    }} title="Eliminar Nota" size="sm" data-id="element-3351" data-mp="2env1pqwba7sf">
        
          <div className="space-y-4" data-id="element-3352" data-mp="2ojydlogytksk">
            <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg" data-id="element-3353" data-mp="2oiwpaiaxv1sw">
              <AlertTriangleIcon className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" data-id="element-3354" data-mp="2tkm325pjptko" />
              <div data-id="element-3355" data-mp="2xitkz3vqx0xg">
                <p className="text-sm font-medium text-red-800 dark:text-red-200" data-id="element-3356" data-mp="32jzg18cvp0a5">
                  {'¿Eliminar esta nota de seguimiento?'}
                </p>
                <p className="text-sm text-red-600 dark:text-red-400 mt-1" data-id="element-3357" data-mp="37b7nhyunebko">
                  {'Esta acción no se puede deshacer.'}
                </p>
              </div>
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg" data-id="element-3358" data-mp="3ha6msqtiwuv0">
              <p className="text-sm font-medium text-gray-900 dark:text-white" data-id="element-3359" data-mp="3l9p8vpx2zbzf">
                {deleteNote.contactName}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-3360" data-mp="ms4p96by4cxn">
                {deleteNote.subject}
              </p>
            </div>
            <div className="flex justify-end gap-3 pt-2" data-id="element-3361" data-mp="vmstct5sbokv">
              <Button variant="outline" onClick={() => {
            setIsDeleteConfirmOpen(false);
            setDeleteNoteId(null);
          }} data-id="element-3362" data-mp="vodoajw0h9j9">
              
                {'Cancelar'}
              </Button>
              <Button variant="danger" onClick={handleDelete} data-id="element-3363" data-mp="3wy2gs2a3qu3">
                {'Eliminar'}
              </Button>
            </div>
          </div>
        </Modal>}

      {/* Excel Export Modal */}
      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Notas de Seguimiento" isExporting={isExporting} data-id="element-3364" data-mp="ctf67a8bcmft" />
      

      {/* Pdf Export Modal */}
      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Notas a PDF" isExporting={isPdfExporting} data-id="element-3365" data-mp="1jcdffkx8j1yd" />
      
    </div>;
}